import { Box, Button, Divider, Grid, TextField } from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import LoadingWrapper from "components/Shared/LoadingWrapper";
import React, { useEffect, useState } from "react";

const AddressForm = ({
  addressDetail = null,
  isBusy = false,
  onSubmit = () => {},
  addressCode,
  coordinates,
  onCancel = () => {},
}) => {
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
  } = useForm({
    defaultValues: {
      code: addressCode || "",
      coordinates: coordinates ? `${coordinates.lat}, ${coordinates.lng}` : "",
      uniqueName: "",
      reference: "",
      description: "",
    },
  });

  useEffect(() => {
    if (addressDetail) {
      reset({
        ...addressDetail,
        coordinates: addressDetail.coordinates
          ? `${addressDetail.coordinates.lat}, ${addressDetail.coordinates.lng}`
          : "",
      });
    }
  }, [addressDetail, reset]);

  const handleOnAddressSubmit = (data) => {
    const { coordinates, ...rest } = data;
    const [lat, lng] = coordinates.split(",").map(parseFloat);

    const payload = {
      coordinates: { lat, lng },
      ...rest,
    };
    onSubmit(payload);
  };

  const [initialValue, setInitialValue] = useState("");

  const generateRandomUUID = () => {
    const randomDigits = Math.floor(1000000000 + Math.random() * 9000000000);
    return `CAN-ADDRESS-${randomDigits}`;
  };

  useEffect(() => {
    if (addressDetail && addressDetail.uniqueName) {
      setInitialValue(addressDetail.uniqueName);
      setValue("uniqueName", addressDetail.uniqueName);
    } else {
      const uuid = generateRandomUUID();
      setInitialValue(uuid);
      setValue("uniqueName", uuid);
    }
  }, [addressDetail, setValue]);

  const renderTextField = (name, label, options = {}, additionalProps = {}) => (
    <Controller
      name={name}
      control={control}
      rules={options}
      render={({ field }) => (
        <TextField
          fullWidth
          label={label}
          size="small"
          error={!!errors[name]}
          helperText={errors[name]?.message}
          {...field}
          {...additionalProps}
        />
      )}
    />
  );

  return (
    <LoadingWrapper loading={isBusy}>
      <Box padding={3}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            {renderTextField(
              "code",
              "Address Code",
              { required: "Address Code is required" },
              {
                InputProps: { readOnly: true },
              }
            )}
          </Grid>

          <Grid item xs={12} md={6}>
            {renderTextField(
              "coordinates",
              "Coordinates",
              { required: "Coordinates are required" },
              {
                placeholder: "28.937657, 70.350585",
                InputProps: { readOnly: true },
              }
            )}
          </Grid>

          <Grid item xs={12}>
            <Controller
              name="uniqueName"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Location Name"
                  value={field.value || initialValue}
                  variant="outlined"
                  fullWidth
                  InputProps={{
                    style: { textTransform: "uppercase" },
                  }}
                  onChange={(e) => {
                    const input = e.target.value;
                    const transformedValue = input.toUpperCase();
                    setInitialValue(transformedValue);
                    setValue("uniqueName", transformedValue);
                    field.onChange(transformedValue);
                  }}
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            {renderTextField("reference", "Reference")}
          </Grid>

          <Grid item xs={12}>
            {renderTextField(
              "description",
              "Description",
              {},
              { multiline: true, rows: 4 }
            )}
          </Grid>
        </Grid>
      </Box>

      <Divider />

      <Box
        paddingX={3}
        paddingY={2}
        display="flex"
        justifyContent="flex-end"
        gap={2}
      >
        <Button disabled={isBusy} onClick={onCancel}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={isBusy}
          onClick={handleSubmit(handleOnAddressSubmit)}
        >
          {addressDetail ? "Update" : "Add"}
        </Button>
      </Box>
    </LoadingWrapper>
  );
};

export default AddressForm;
